import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import {
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Modal,
  Badge,
  Spinner,
  Alert,
} from 'react-bootstrap'
import { ParentSize } from '@visx/responsive'
import { XYChart, Axis, Grid, LineSeries, Tooltip } from '@visx/xychart'
import apiServices from '../services/apiServices'

const DEFAULT_START_DATE = moment().startOf('month').format('YYYY-MM-DD')
const DEFAULT_END_DATE = moment().format('YYYY-MM-DD')
const INTEGER_FORMAT = { maximumFractionDigits: 0 }
const EMPTY_DETAIL_SUMMARY = {
  totalSpend: 0,
  totalClicks: 0,
  totalImpressions: 0,
  totalCampaigns: 0,
}

const createEmptyDetailData = () => ({
  summary: { ...EMPTY_DETAIL_SUMMARY },
  campaigns: [],
  trend: [],
  campaignTrends: [],
  firstDate: '',
  lastDate: '',
})

const formatNumber = (value, options = {}) => {
  const numeric = Number(value) || 0
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: options.minimumFractionDigits || 0,
    maximumFractionDigits:
      options.maximumFractionDigits !== undefined
        ? options.maximumFractionDigits
        : 2,
  })
}

const formatDateLabel = (value) => {
  if (!value) return '-'
  return moment(value).format('YYYY/MM/DD')
}

const formatDateRange = (start, end) =>
  `${formatDateLabel(start)} ~ ${formatDateLabel(end)}`

const INSIGHT_SERIES = [
  {
    key: 'cpc',
    label: 'CPC (原幣)',
    color: '#0d6efd',
    formatter: (value) => `${formatNumber(value)} 原幣`,
  },
  {
    key: 'cpm',
    label: 'CPM (原幣)',
    color: '#6f42c1',
    formatter: (value) => `${formatNumber(value)} 原幣`,
  },
]

const computeCpc = (spend, clicks) => {
  if (!clicks) return 0
  return Number(spend) / Number(clicks)
}

const computeCpm = (spend, impressions) => {
  if (!impressions) return 0
  return (Number(spend) / Number(impressions)) * 1000
}

function TrendChart({ data }) {
  if (!data?.length) {
    return <div className="text-muted">目前範圍內沒有趨勢資料</div>
  }

  const normalizedData = data.map((datum) => ({
    ...datum,
    cpc: computeCpc(datum?.spend, datum?.clicks),
    cpm: computeCpm(datum?.spend, datum?.impressions),
  }))

  const accessor = (key) => (datum) => Number(datum?.[key]) || 0
  const xAccessor = (datum) => {
    if (!datum?.date) return new Date()
    return new Date(`${datum.date}T00:00:00`)
  }

  return (
    <div style={{ height: 320 }}>
      <ParentSize>
        {({ width, height }) => (
          <XYChart
            width={width}
            height={height}
            xScale={{ type: 'time' }}
            yScale={{ type: 'linear' }}
            margin={{ top: 20, left: 60, right: 20, bottom: 40 }}
          >
            <Grid columns={false} numTicks={5} />
            <Axis orientation="bottom" />
            <Axis orientation="left" />
            {INSIGHT_SERIES.map((series) => (
              <LineSeries
                key={series.key}
                dataKey={series.label}
                data={normalizedData}
                xAccessor={xAccessor}
                yAccessor={accessor(series.key)}
                stroke={series.color}
              />
            ))}
            <Tooltip
              showSeriesGlyphs
              renderTooltip={({ tooltipData }) => {
                const datum = tooltipData?.nearestDatum?.datum
                if (!datum) return null
                return (
                  <div>
                    <div className="fw-semibold mb-2">
                      {formatDateLabel(datum.date)}
                    </div>
                    {INSIGHT_SERIES.map((series) => (
                      <div
                        key={series.key}
                        className="d-flex justify-content-between"
                      >
                        <span>{series.label}</span>
                        <span className="fw-semibold">
                          {series.formatter
                            ? series.formatter(accessor(series.key)(datum))
                            : formatNumber(accessor(series.key)(datum))}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              }}
            />
          </XYChart>
        )}
      </ParentSize>
    </div>
  )
}

TrendChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      spend: PropTypes.number,
      clicks: PropTypes.number,
      impressions: PropTypes.number,
    })
  ),
}

TrendChart.defaultProps = {
  data: [],
}

function StatCard({ title, value, subtitle }) {
  return (
    <Card className="shadow-sm mb-3">
      <Card.Body>
        <div className="text-muted small">{title}</div>
        <div className="fs-4 fw-bold">{value}</div>
        {subtitle ? <div className="text-muted small">{subtitle}</div> : null}
      </Card.Body>
    </Card>
  )
}

StatCard.defaultProps = {
  subtitle: '',
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.string,
}

export default function AccountInsights() {
  const [accountsData, setAccountsData] = useState({
    rows: [],
    totals: { totalSpend: 0, totalClicks: 0, totalImpressions: 0 },
    providerOptions: [],
    totalAccounts: 0,
    startDate: DEFAULT_START_DATE,
    endDate: DEFAULT_END_DATE,
  })
  const [filters, setFilters] = useState({
    startDate: DEFAULT_START_DATE,
    endDate: DEFAULT_END_DATE,
    provider: '',
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [selectedAccount, setSelectedAccount] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [detailData, setDetailData] = useState(createEmptyDetailData())
  const campaignTrendIndex = useMemo(() => {
    const index = {}
    detailData.campaignTrends.forEach((trend) => {
      if (trend?.campaignId) {
        index[trend.campaignId] = trend
      }
    })
    return index
  }, [detailData.campaignTrends])

  const loadAccounts = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await apiServices.data({
        path: 'billing/insight-accounts',
        method: 'get',
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          provider: filters.provider || undefined,
        },
      })
      if (response?.error) {
        throw response.error
      }
      setAccountsData({
        rows: response?.rows || [],
        totals: response?.totals || {
          totalSpend: 0,
          totalClicks: 0,
          totalImpressions: 0,
        },
        providerOptions: response?.providerOptions || [],
        totalAccounts: response?.totalAccounts || 0,
        startDate: response?.startDate || filters.startDate,
        endDate: response?.endDate || filters.endDate,
      })
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || '載入帳號成效資料失敗'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadAccounts()
  }, [loadAccounts])

  const filteredAccounts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return accountsData.rows
    return accountsData.rows.filter((row) =>
      [row.accountName, row.accountId, row.provider]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    )
  }, [accountsData.rows, searchTerm])

  const handleAccountDetail = useCallback(
    async (account) => {
      if (!account) return
      setSelectedAccount(account)
      setShowModal(true)
      setDetailError('')
      setDetailLoading(true)
      try {
        const response = await apiServices.data({
          path: `billing/insight-accounts/${account.accountId}`,
          method: 'get',
          params: {
            startDate: filters.startDate,
            endDate: filters.endDate,
            provider: account.provider,
          },
        })
        if (response?.error) {
          throw response.error
        }
        setDetailData({
          summary: response?.summary || { ...EMPTY_DETAIL_SUMMARY },
          campaigns: response?.campaigns || [],
          trend: response?.trend || [],
          campaignTrends: response?.campaignTrends || [],
          firstDate: response?.firstDate || filters.startDate,
          lastDate: response?.lastDate || filters.endDate,
        })
      } catch (err) {
        const message =
          err?.response?.data?.message || err?.message || '載入帳號成效細節失敗'
        setDetailError(message)
      } finally {
        setDetailLoading(false)
      }
    },
    [filters.endDate, filters.startDate]
  )

  const providerOptions = useMemo(() => {
    const extras = (accountsData.providerOptions || []).map((provider) => ({
      value: provider,
      label: provider ? provider.toUpperCase() : '未分類',
    }))
    return [{ value: '', label: '全部媒體' }, ...extras]
  }, [accountsData.providerOptions])

  const closeModal = () => {
    setShowModal(false)
    setDetailData(createEmptyDetailData())
  }

  const renderDetailBody = () => {
    if (detailLoading) {
      return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )
    }
    if (detailError) {
      return <Alert variant="warning">{detailError}</Alert>
    }
    return (
      <>
        <Row className="g-3 mb-4">
          <Col md={3} sm={6}>
            <StatCard
              title="總花費"
              value={`${formatNumber(detailData.summary.totalSpend)} 原幣`}
              subtitle="目前範圍"
            />
          </Col>
          <Col md={3} sm={6}>
            <StatCard
              title="總點擊"
              value={formatNumber(
                detailData.summary.totalClicks,
                INTEGER_FORMAT
              )}
              subtitle="目前範圍"
            />
          </Col>
          <Col md={3} sm={6}>
            <StatCard
              title="總曝光"
              value={formatNumber(
                detailData.summary.totalImpressions,
                INTEGER_FORMAT
              )}
              subtitle="目前範圍"
            />
          </Col>
          <Col md={3} sm={6}>
            <StatCard
              title="Campaign 數"
              value={`${detailData.summary.totalCampaigns} 個`}
              subtitle={formatDateRange(
                detailData.firstDate,
                detailData.lastDate
              )}
            />
          </Col>
        </Row>

        <div className="d-flex flex-column gap-3 mb-4">
          {detailData.campaigns.length ? (
            detailData.campaigns.map((campaign) => {
              const trendEntry = campaignTrendIndex[campaign.campaignId]
              const chartData = trendEntry?.data || []
              const chartRange = chartData.length
                ? formatDateRange(
                    chartData[0]?.date,
                    chartData[chartData.length - 1]?.date
                  )
                : null
              return (
                <Card
                  key={`campaign-trend-${campaign.campaignId}`}
                  className="shadow-sm"
                >
                  <Card.Header>
                    <div className="d-flex flex-column flex-md-row justify-content-between">
                      <div>
                        <div className="fw-semibold">
                          {campaign.campaignName || '未命名 Campaign'}
                        </div>
                        <div className="text-muted small">
                          {campaign.campaignId}
                        </div>
                      </div>
                      <div className="text-muted small mt-2 mt-md-0 text-md-end">
                        幣別：{campaign.currency} ｜ 花費：
                        {formatNumber(campaign.spend)} ｜ 點擊：
                        {formatNumber(campaign.clicks, INTEGER_FORMAT)} ｜
                        曝光：
                        {formatNumber(campaign.impressions, INTEGER_FORMAT)}
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="text-muted small mb-2">
                      顯示 Campaign 全期間
                      {chartRange ? `（${chartRange}）` : '（無日期資料）'}
                    </div>
                    <TrendChart data={chartData} />
                  </Card.Body>
                </Card>
              )
            })
          ) : (
            <Card className="shadow-sm">
              <Card.Body className="text-center text-muted">
                目前沒有可視化的 Campaign 資料
              </Card.Body>
            </Card>
          )}
        </div>

        <Card className="shadow-sm">
          <Card.Header>
            <div className="fw-semibold">Campaign 明細</div>
            <div className="text-muted small">
              依花費排序，共 {detailData.summary.totalCampaigns} 個
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {detailData.campaigns.length ? (
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr className="text-nowrap">
                      <th style={{ minWidth: '280px' }}>Campaign</th>
                      <th style={{ width: '140px' }}>幣別</th>
                      <th className="text-end" style={{ width: '160px' }}>
                        花費
                      </th>
                      <th className="text-end" style={{ width: '140px' }}>
                        點擊
                      </th>
                      <th className="text-end" style={{ width: '160px' }}>
                        曝光
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailData.campaigns.map((campaign) => (
                      <tr key={campaign.campaignId}>
                        <td>
                          <div className="fw-semibold">
                            {campaign.campaignName || '未命名 Campaign'}
                          </div>
                          <div className="text-muted small">
                            {campaign.campaignId}
                          </div>
                        </td>
                        <td>{campaign.currency}</td>
                        <td className="text-end fw-semibold">
                          {formatNumber(campaign.spend)}
                        </td>
                        <td className="text-end">
                          {formatNumber(campaign.clicks, INTEGER_FORMAT)}
                        </td>
                        <td className="text-end">
                          {formatNumber(campaign.impressions, INTEGER_FORMAT)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4 text-muted">
                目前沒有 Campaign 明細
              </div>
            )}
          </Card.Body>
        </Card>
      </>
    )
  }

  return (
    <div className="container-fluid py-3">
      <Row className="g-3 align-items-end mb-3">
        <Col md={3} sm={6}>
          <Form.Group controlId="insightStartDate">
            <Form.Label>開始日期</Form.Label>
            <Form.Control
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, startDate: e.target.value }))
              }
            />
          </Form.Group>
        </Col>
        <Col md={3} sm={6}>
          <Form.Group controlId="insightEndDate">
            <Form.Label>結束日期</Form.Label>
            <Form.Control
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, endDate: e.target.value }))
              }
            />
          </Form.Group>
        </Col>
        <Col md={3} sm={6}>
          <Form.Group controlId="insightProvider">
            <Form.Label>媒體來源</Form.Label>
            <Form.Select
              value={filters.provider}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, provider: e.target.value }))
              }
            >
              {providerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md="auto" sm={6}>
          <Button variant="primary" onClick={loadAccounts} disabled={loading}>
            {loading ? '載入中…' : '重新整理'}
          </Button>
        </Col>
        <Col md={3} sm={6}>
          <Form.Group controlId="accountSearch">
            <Form.Label>搜尋帳號</Form.Label>
            <Form.Control
              type="text"
              placeholder="輸入帳號名稱 / ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3 mb-3">
        <Col md={3} sm={6}>
          <StatCard
            title="帳號數"
            value={`${accountsData.totalAccounts} 個`}
            subtitle={formatDateRange(
              accountsData.startDate,
              accountsData.endDate
            )}
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            title="總花費"
            value={`${formatNumber(accountsData.totals.totalSpend)} 原幣`}
            subtitle="目前範圍"
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            title="總點擊"
            value={formatNumber(
              accountsData.totals.totalClicks,
              INTEGER_FORMAT
            )}
            subtitle="目前範圍"
          />
        </Col>
        <Col md={3} sm={6}>
          <StatCard
            title="總曝光"
            value={formatNumber(
              accountsData.totals.totalImpressions,
              INTEGER_FORMAT
            )}
            subtitle="目前範圍"
          />
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Header>
          <div className="d-flex flex-column flex-md-row justify-content-between">
            <div>
              <div className="fw-semibold">帳號成效列表</div>
              <div className="text-muted small">
                篩選區間：
                {formatDateRange(accountsData.startDate, accountsData.endDate)}
              </div>
            </div>
            <div className="text-muted small">
              共 {filteredAccounts.length} / {accountsData.totalAccounts} 個帳號
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <div className="text-muted mt-2">資料載入中…</div>
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: '70vh' }}>
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr className="text-nowrap">
                    <th style={{ minWidth: '240px' }}>帳號名稱</th>
                    <th style={{ width: '160px' }}>帳號 ID</th>
                    <th style={{ width: '140px' }}>媒體</th>
                    <th style={{ width: '140px' }}>幣別</th>
                    <th className="text-end" style={{ width: '160px' }}>
                      花費
                    </th>
                    <th className="text-end" style={{ width: '140px' }}>
                      點擊
                    </th>
                    <th className="text-end" style={{ width: '160px' }}>
                      曝光
                    </th>
                    <th style={{ width: '200px' }}>資料範圍</th>
                    <th className="text-center" style={{ width: '120px' }}>
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.length ? (
                    filteredAccounts.map((row) => (
                      <tr key={`${row.provider}-${row.accountId}`}>
                        <td>
                          <div className="fw-semibold">
                            {row.accountName || '未命名帳號'}
                          </div>
                          <div className="text-muted small">
                            {row.provider?.toUpperCase() || '未提供'}
                          </div>
                        </td>
                        <td>{row.accountId}</td>
                        <td>
                          <Badge bg="secondary">
                            {row.provider?.toUpperCase() || '未分類'}
                          </Badge>
                        </td>
                        <td>{row.currency || '-'}</td>
                        <td className="text-end fw-semibold">
                          {formatNumber(row.totalSpend)}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.totalClicks, INTEGER_FORMAT)}
                        </td>
                        <td className="text-end">
                          {formatNumber(row.totalImpressions, INTEGER_FORMAT)}
                        </td>
                        <td className="text-muted small">
                          {formatDateRange(row.firstDate, row.lastDate)}
                        </td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleAccountDetail(row)}
                          >
                            查看活動
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        沒有符合條件的帳號
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal size="xl" show={showModal} onHide={closeModal} centered scrollable>
        <Modal.Header closeButton>
          <div className="d-flex flex-column flex-md-row w-100 justify-content-between">
            <div>
              <Modal.Title className="mb-0">
                {selectedAccount?.accountName || '未命名帳號'}
              </Modal.Title>
              <div className="text-muted small">
                {selectedAccount?.accountId} ｜ 媒體：
                {selectedAccount?.provider?.toUpperCase() || '未提供'}
              </div>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={detailLoading}
              onClick={() => handleAccountDetail(selectedAccount)}
            >
              {detailLoading ? '更新中…' : '重新整理'}
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>{renderDetailBody()}</Modal.Body>
      </Modal>
    </div>
  )
}
